import React, { useState } from "react";
import {
  IconButton,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  RadioGroup,
  FormControlLabel,
  Radio,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ReactPlayer from "react-player";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ChatIcon from "@mui/icons-material/Chat";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CancelIcon from "@mui/icons-material/Cancel";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { ArrowBack } from "@mui/icons-material";

import {
  capitalizeFirstLetter,
  getReadableDateString,
  secondsToHMS,
} from "../../utils/commons";
import {
  createVideo,
  getSourceVideoDetailsErrorMessage,
  getSourceVideoDetails,
  sendPutRequest,
} from "../../services/ContentServices";
import { enqueueSnackbar } from "notistack";
import { uploadVideoThumbnail } from "../../services/ContentServices";
import {
  inputVideoIdStyles,
  thumbnailUploadStyles,
  videoPlayerContaierStyles,
  videoPlayerStyles,
} from "./css";

interface AddVideoProps {
  setShowAddVideo: React.Dispatch<React.SetStateAction<any>>;
}

const AddVideoComponent: React.FC<AddVideoProps> = ({ setShowAddVideo }) => {
  const theme = useTheme();
  const [title, setTitle] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [videoType, setVideoType] = useState<string>("");
  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [currthumbnailUrl, setCurrthumbnailUrl] = useState<string>("");
  const [videoId, setVideoId] = useState<string>("");
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [duration, setDuration] = useState<number>(0);
  const [addedOn, setAddedOn] = useState<string>("");
  const [isfetching, setIsfetching] = useState<boolean>(false);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const resetFields = () => {
    setVideoType("");
    setThumbnail(null);
    setDescription("");
    setVideoId("");
    setTitle("");
    setVideoUrl("");
    setDuration(0);
    setAddedOn("");
    setCurrthumbnailUrl("");
  };

  const handleThumbnailChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;
      setThumbnail(file);
      const url = URL.createObjectURL(file);
      setCurrthumbnailUrl(url);
    } catch (error) {
      console.error("Error uploading thumbnail:", error);
    }
  };

  const saveVideo = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (!thumbnail) {
        return enqueueSnackbar("Thumbnail is required");
      }
      if (!title || !videoUrl || !videoType || !duration) {
        return enqueueSnackbar("Please fill in all fields");
      }
      if (videoType !== "reel" && videoType !== "horizontal") {
        return enqueueSnackbar("Invalid video type");
      }
      setIsUploading(true);
      const signUrlResponse = await uploadVideoThumbnail(thumbnail.name);
      const signUrl = signUrlResponse.data?.data as string;
      const thumbAwsUrl = signUrl.split("?")[0];
      const upload = await sendPutRequest({ signUrl, file: thumbnail });
      if (upload.status == 200) {
        const bodyParam = {
          title: capitalizeFirstLetter(title) as string,
          description: description as string,
          url: videoUrl as string,
          sourceVideoId: videoId as string,
          thumbnailUrl: thumbAwsUrl as string,
          type: videoType as string,
          duration: duration as number,
        };
        const result = await createVideo(bodyParam);
        if (result.status === 201) {
          enqueueSnackbar("Video Created");
          setShowAddVideo(false);
          setIsUploading(false);
          resetFields();
        }
        return;
      } else {
        enqueueSnackbar("Failed to save video. Please try again later");
      }
    } catch (error) {
      setIsUploading(false);
      console.error("Error saving video:", error);
      enqueueSnackbar("Failed to save video. Please try again later");
    }
  };

  const fetchVideoDetails = async () => {
    if (!videoId) return;
    try {
      setIsfetching(true);
      const data = await getSourceVideoDetails({ videoId });
      setIsfetching(false);
      setVideoUrl(data.playbackUrl);
      setDuration(data.duration);
      setAddedOn(data.createdAt);
      const videoHeight = data.height;
      const videoWidth = data.width;
      videoHeight > videoWidth
        ? setVideoType("reel")
        : setVideoType("horizontal");
    } catch (error) {
      setIsfetching(false);
      console.error("There was a problem with the fetch operation:", error);
      enqueueSnackbar(getSourceVideoDetailsErrorMessage(error), {
        variant: "error",
      });
    }
  };

  return (
    <Box padding="10px">
      <Grid container alignItems="center" marginTop="20px">
        <Grid item xs={12} md={6}>
          <Box
            display={"flex"}
            justifyContent={"flex-start"}
            alignItems={"center"}
          >
            <IconButton
              edge="start"
              aria-label="back"
              sx={{
                marginRight: theme.spacing(1),
                marginLeft: theme.spacing(1),
              }}
              onClick={() => {
                setShowAddVideo(false);
              }}
            >
              <ArrowBack />
            </IconButton>
            <Typography
              variant="h5"
              style={{
                color: theme.palette.text.primary,
                marginRight: theme.spacing(3),
              }}
            >
              Add Video
            </Typography>
            <TextField
              style={{
                ...inputVideoIdStyles,
                marginRight: theme.spacing(1),
              }}
              value={videoId}
              onChange={(e) => setVideoId(e.target.value.trim())}
              InputProps={{ style: { height: 40, textAlign: "center" } }}
              placeholder="Enter Video ID (from cloudflare)"
            />

            <Button
              variant="outlined"
              color="primary"
              style={{
                height: "40px",
                position: "relative",
              }}
              disabled={isfetching}
              onClick={fetchVideoDetails}
            >
              {isfetching && (
                <CircularProgress
                  style={{
                    width: "20px",
                    height: "20px",
                  }}
                  color="inherit"
                />
              )}
              {isfetching ? "" : "Get Video"}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display={"flex"} justifyContent={"flex-end"}>
            <Button
              variant="contained"
              color="secondary"
              style={{ marginRight: "10px" }}
              onClick={resetFields}
            >
              Discard
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!videoUrl || isUploading}
              onClick={saveVideo}
            >
              {isUploading ? (
                <CircularProgress
                  style={{
                    width: "20px",
                    height: "20px",
                  }}
                  color="inherit"
                />
              ) : (
                "Save"
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>

      <Box padding="20px">
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box
              boxShadow={2}
              padding={2}
              borderRadius={5}
              sx={{ scrollbarWidth: "none" }}
              maxHeight={"73vh"}
              overflow={"auto"}
            >
              <TextField
                placeholder="Enter Title"
                fullWidth
                value={title}
                margin="normal"
                onChange={(e) => setTitle(e.target.value)}
              />
              <TextField
                placeholder="Enter Description"
                fullWidth
                multiline
                rows={4}
                margin="normal"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <Box marginTop={2}>
                <Typography sx={{ color: theme.palette.text.secondary }}>
                  Video Type
                </Typography>
                <RadioGroup
                  value={videoType}
                  onChange={(e) => setVideoType(e.target.value)}
                  row
                >
                  <Grid container alignItems="center">
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="reel"
                        control={<Radio />}
                        label="Reel/Short Video"
                        sx={{ "& .MuiIconButton-root": { borderRadius: 0 } }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <FormControlLabel
                        value="horizontal"
                        control={<Radio />}
                        label="Horizontal Video"
                        sx={{ "& .MuiIconButton-root": { borderRadius: 0 } }}
                      />
                    </Grid>
                  </Grid>
                </RadioGroup>
              </Box>
              <Box marginTop={2}>
                <Typography
                  sx={{ color: theme.palette.text.secondary, marginBottom: 2 }}
                >
                  Upload Thumbnail
                </Typography>
                {currthumbnailUrl ? (
                  <Box sx={thumbnailUploadStyles}>
                    <img
                      src={currthumbnailUrl}
                      width={"100%"}
                      height={"100%"}
                    />
                    <IconButton
                      sx={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                      }}
                      onClick={() => setCurrthumbnailUrl("")}
                    >
                      <CancelIcon
                        sx={{
                          color: "gray",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                      />
                    </IconButton>
                  </Box>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="image/jpeg, image/jpg, image/png"
                      onChange={handleThumbnailChange}
                      style={{ display: "none" }}
                      id="thumbnail-upload"
                    />
                    <label htmlFor="thumbnail-upload">
                      <Box
                        component="div"
                        sx={{
                          border: `1px dashed ${theme.palette.primary.main}`,
                          borderRadius: theme.shape.borderRadius,
                          padding: theme.spacing(2),
                          marginTop: theme.spacing(1),
                          cursor: "pointer",
                        }}
                      >
                        <Typography sx={{ color: theme.palette.text.disabled }}>
                          Choose a file
                        </Typography>
                      </Box>
                    </label>
                  </>
                )}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box boxShadow={2} padding="1rem" borderRadius="5px">
              <Box sx={videoPlayerContaierStyles}>
                <Box style={videoPlayerStyles}>
                  <ReactPlayer
                    url={videoUrl}
                    controls
                    width="100%"
                    height="100%"
                  />
                </Box>
              </Box>

              <Box display="flex" alignItems="center" marginTop={2}>
                <Box display="flex" alignItems="center">
                  <IconButton style={{ color: "gray" }}>
                    <FavoriteIcon />
                  </IconButton>
                  <Typography
                    variant="body1"
                    style={{ marginRight: theme.spacing(1) }}
                  >
                    0
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" marginLeft={2}>
                  <IconButton style={{ color: "gray" }}>
                    <ChatIcon />
                  </IconButton>
                  <Typography
                    variant="body1"
                    style={{ marginRight: theme.spacing(1) }}
                  >
                    0
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" marginLeft={2}>
                  <IconButton style={{ color: "gray" }}>
                    <VisibilityIcon />
                  </IconButton>
                  <Typography
                    variant="body1"
                    style={{ marginRight: theme.spacing(1) }}
                  >
                    0
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" marginLeft={2}>
                  <IconButton style={{ color: "gray" }}>
                    <AccessTimeIcon />
                  </IconButton>
                  <Typography
                    variant="body1"
                    style={{ marginRight: theme.spacing(1) }}
                  >
                    {secondsToHMS(duration) || "00:00:00"}
                  </Typography>
                </Box>
              </Box>

              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontFamily: "monospace",
                  paddingTop: 2,
                }}
              >
                Added On : {addedOn && getReadableDateString(addedOn)}
              </Typography>
            </Box>

            <Box boxShadow={2} padding={"1rem"} borderRadius={"5px"}>
              <Typography variant="h6">Playlists</Typography>
              <Typography fontSize={10} color={theme.palette.text.secondary}>
                Add your video to one or more playlists to organize your content
                for viewers
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default AddVideoComponent;
